import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPageContent } from '../../redux/actions/pagesAction';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../App';
import { FiLock, FiCalendar } from 'react-icons/fi';
import './Privacy.css';

const Privacy = () => {
  const { t, i18n } = useTranslation('global');
  const dispatch = useDispatch();
  const { isDarkMode } = useDarkMode();

  const pageKey = 'privacy';
  const pageState = useSelector((state) => state.pages.pages[pageKey]);
  const { data, isLoading, error } = pageState || {};

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(fetchPageContent({ pageName: pageKey, lang: i18n.language }));
  }, [dispatch, i18n.language]);

  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  const contentVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { delay: 0.2, duration: 0.6 } }
  };

  // Helper to format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className={`privacy-page ${isDarkMode ? 'dark' : ''}`} dir={i18n.dir()}>
      <div className="privacy-bg-overlay"></div>
      
      <div className="container py-5">
        {isLoading ? (
          <div className="privacy-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="privacy-error text-center">
            <h3 className="text-danger">{t('common.error', 'Error occurred')}</h3>
            <p>{typeof error === 'string' ? error : t('common.tryAgain', 'Please try again later.')}</p>
          </div>
        ) : data ? (
          <motion.div 
            className="privacy-content-wrapper main-glass"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <div className="privacy-header mb-4">
              <div className="icon-wrapper mb-3">
                <FiLock size={40} className="text-primary" />
              </div>
              <h1 className="display-5 fw-bold gradient-text">
                {data.title}
              </h1>
              {data.updated_at && (
                <div className="privacy-meta text-muted mt-2">
                  <FiCalendar className="me-2" />
                  <span>{t('terms.lastUpdated', 'Last Updated')}: {formatDate(data.updated_at)}</span>
                </div>
              )}
            </div>

            <motion.div 
              className="privacy-body"
              variants={contentVariants}
              dangerouslySetInnerHTML={{ __html: data.content }}
            />
          </motion.div>
        ) : null}
      </div>
    </div>
  );
};

export default Privacy;
